import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { User, AuthState } from '../types';
import { authApi, handleApiError } from '../services/api';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (userData: Partial<User> & { password: string }) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    // Check for existing auth token and validate it
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('auth_token');
        
        if (token) {
          // Validate token with real API
          const response = await authApi.me();
          
          setAuthState({
            user: {
              id: response.user.id.toString(),
              email: response.user.email,
              firstName: response.user.firstName,
              lastName: response.user.lastName,
              role: response.user.role,
              createdAt: new Date(response.user.createdAt),
            },
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        // Token is invalid, remove it
        localStorage.removeItem('authToken');
        localStorage.removeItem('auth_token');
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      // Call real API for authentication
      const result = await authApi.login(email, password);
      
      // Store the token
      localStorage.setItem('authToken', result.token);
      localStorage.setItem('auth_token', result.token); // Also store in alternate key for compatibility
      
      // Set user state
      setAuthState({
        user: {
          id: result.user.id.toString(),
          email: result.user.email,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          role: result.user.role,
          createdAt: new Date(result.user.createdAt),
        },
        isAuthenticated: true,
        isLoading: false,
      });
      
      return true;
    } catch (error) {
      console.error('Login error:', handleApiError(error));
      return false;
    }
  };

  const register = async (userData: Partial<User> & { password: string }): Promise<boolean> => {
    try {
      // Call real API for registration
      const result = await authApi.login(userData.email!, userData.password); // For now, just login after registration
      
      // Store the token
      localStorage.setItem('authToken', result.token);
      localStorage.setItem('auth_token', result.token);
      
      // Set user state
      setAuthState({
        user: {
          id: result.user.id.toString(),
          email: result.user.email,
          firstName: result.user.firstName,
          lastName: result.user.lastName,
          role: result.user.role,
          createdAt: new Date(result.user.createdAt),
        },
        isAuthenticated: true,
        isLoading: false,
      });
      
      return true;
    } catch (error) {
      console.error('Register error:', handleApiError(error));
      return false;
    }
  };

  const logout = () => {
    // Call API logout and remove tokens
    authApi.logout().catch(console.error); // Don't wait for this
    
    localStorage.removeItem('authToken');
    localStorage.removeItem('auth_token');
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const value: AuthContextType = {
    ...authState,
    login,
    logout,
    register,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};